import React, { Suspense } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { PrimaryHeader } from './PrimaryHeader';
import { SecondaryHeader } from './SecondaryHeader';
import { GlobalBannerStrip } from '../banners/GlobalBannerStrip';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { useAuth } from '../../contexts/AuthContext';
import { PatientLookupProvider } from '../../contexts/PatientLookupContext';
import { useGhlLocationSync } from '../../hooks/useGhlLocationSync';
import {
  CallTrackerSkeleton, DashboardSkeleton, LeadsSkeleton,
  SchedulingSkeleton, GenericPageSkeleton,
} from '../ui/skeletons';
import {
  TrendingUp,
  MessageSquare,
  ListTodo,
  Phone,
  MapPin,
  Users,
  UserPlus,
  Search,
  Settings,
  Shield,
  Building2,
  BarChart3,
  Calendar,
  Clock,
  Layers,
  BookOpen,
  UserCheck,
  ShieldX,
  HeartPulse,
  PhoneMissed,
  PhoneForwarded,
  ClipboardList,
  Target,
} from 'lucide-react';

// Define secondary navigation tabs for each module
const moduleSecondaryTabs = {
  '/dashboard': [
    { id: 'hq', path: '/dashboard?tab=hq', label: 'HQ Data', icon: Building2, default: true },
    { id: 'leads', path: '/dashboard?tab=leads', label: 'Leads', icon: Users },
    { id: 'communication', path: '/dashboard?tab=communication', label: 'Communication', icon: MessageSquare },
    { id: 'calls', path: '/dashboard?tab=calls', label: 'Calls', icon: Phone },
  ],
  '/leads': [
    { id: 'all', path: '/leads', label: 'All Leads', icon: Users },
    { id: 'new', path: '/leads?status=New', label: 'New', icon: UserPlus },
    { id: 'contacted', path: '/leads?status=Contacted', label: 'Contacted', icon: Phone },
    { id: 'unassigned', path: '/leads?status=Unassigned', label: 'Unassigned Status', icon: UserCheck },
    { id: 'analytics', path: '/leads?status=analytics', label: 'Analytics', icon: BarChart3 },
  ],
  '/patients': [],
  '/call-tracker': [
    { id: 'dashboard', path: '/call-tracker?tab=dashboard', label: 'Dashboard', icon: TrendingUp, default: true, requirePerm: ['call_tracker', 'view_analytics'] },
    { id: 'call-logs', path: '/call-tracker?tab=call-logs', label: 'Call Logs', icon: Phone, requirePerm: ['call_tracker', 'view_calls'] },
    { id: 'missed-calls', path: '/call-tracker?tab=missed-calls', label: 'Missed Calls', icon: PhoneMissed, requirePerm: ['call_tracker', 'view_missed'] },
    { id: 'winback', path: '/call-tracker?tab=winback', label: 'Winback', icon: PhoneForwarded, requirePerm: ['call_tracker', 'view_winback'] },
    { id: 'follow-ups', path: '/call-tracker?tab=follow-ups', label: 'Follow-ups', icon: ClipboardList, requirePerm: ['call_tracker', 'view_followups'] },
    { id: 'campaign', path: '/call-tracker?tab=campaign', label: 'Campaign', icon: Target, requirePerm: ['call_tracker', 'view_calls'] },
    { id: 'analytics', path: '/call-tracker?tab=analytics', label: 'Analytics', icon: BarChart3, requirePerm: ['call_tracker', 'view_analytics'] },
    { id: 'agent-reports', path: '/call-tracker?tab=agent-reports', label: 'Agent Reports', icon: Users, requirePerm: ['call_tracker', 'view_agent_reports'] },
  ],
  '/scheduling': [
    { id: 'calendar', path: '/scheduling', label: 'Calendar', icon: Calendar, requirePerm: ['scheduling', 'view_calendar'] },
    { id: 'availability', path: '/availability', label: 'Availability', icon: Clock, requirePerm: ['scheduling', 'manage_availability'] },
    { id: 'overrides', path: '/overrides', label: 'Overrides', icon: Layers, requirePerm: ['scheduling', 'manage_overrides'] },
    { id: 'booking-logs', path: '/booking-logs', label: 'Booking Logs', icon: BookOpen },
    { id: 'analytics', path: '/scheduling-analytics', label: 'Analytics', icon: BarChart3, requirePerm: ['scheduling', 'view_analytics'] },
    { id: 'reviews', path: '/scheduling?view=reviews', label: 'Google Reviews' },
  ],
  '/admin': [],
};

export function HorizontalLayout() {
  const { isAuthenticated, loading, locationAccessDenied, locations, setCurrentLocation, setLocationAccessDenied } = useAuth();
  const location = useLocation();
  useGhlLocationSync();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve intended path so user lands back on the requested page after login
    // (critical for CRM Bridge deep-links like /scheduling, /inbox, etc.)
    const next = location.pathname + (location.search || '');
    const loginUrl = next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login';
    return <Navigate to={loginUrl} replace />;
  }

  // Determine which secondary tabs to show based on current path
  const getSecondaryTabs = () => {
    const path = location.pathname;
    
    // Check for admin routes
    if (path.startsWith('/admin')) {
      return moduleSecondaryTabs['/admin'];
    }
    
    // Check for exact or prefix matches
    for (const [key, tabs] of Object.entries(moduleSecondaryTabs)) {
      if (path === key || (key !== '/dashboard' && path.startsWith(key))) {
        return tabs;
      }
    }
    
    // Booking logs, overrides, availability are under scheduling module
    if (['/booking-logs', '/overrides', '/availability', '/scheduling-analytics'].includes(path)) {
      return moduleSecondaryTabs['/scheduling'];
    }
    
    // Default: dashboard tabs
    if (path === '/' || path === '/dashboard') {
      return moduleSecondaryTabs['/dashboard'];
    }
    
    return null;
  };

  const secondaryTabs = getSecondaryTabs();

  return (
    <PatientLookupProvider>
      <div className="min-h-screen bg-slate-50">
        <ImpersonationBanner />
        <GlobalBannerStrip />
        <PrimaryHeader />
        <SecondaryHeader tabs={secondaryTabs} />

        {/* Access Denied Overlay — shown when GHL switches to an unauthorized location */}
        {locationAccessDenied && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="location-access-denied-overlay">
          {/* Backdrop blur over entire page */}
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" />

          {/* Card */}
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 px-10 py-10 max-w-md w-full mx-4 text-center animate-in fade-in-0 zoom-in-95">
            <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-5">
              <ShieldX className="h-7 w-7 text-red-500" strokeWidth={1.5} />
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-2">No Access to This Location</h2>
            <p className="text-sm text-slate-500 leading-relaxed mb-6">
              You don't have permission to view this location.
              Please switch to a location you have access to using the CRM location switcher, or contact your administrator to request access.
            </p>
            {locations.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-medium">Switch to an accessible location:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {locations.slice(0, 5).map(loc => (
                    <button key={loc.id} onClick={() => { setCurrentLocation(loc); setLocationAccessDenied(false); }}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors">
                      {loc.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <main className={
        location.pathname.startsWith('/patients')
          ? 'p-0'
          : location.pathname.startsWith('/custom/')
            ? 'p-0'
            : 'px-6 py-4'
      }>
        <Suspense fallback={<RouteSkeleton path={location.pathname} />}>
          <div className="animate-in fade-in-0 duration-200">
            <Outlet />
          </div>
        </Suspense>
      </main>
    </div>
    </PatientLookupProvider>
  );
}

function RouteSkeleton({ path }) {
  if (path.startsWith('/call-tracker')) return <CallTrackerSkeleton />;
  if (path === '/' || path === '/dashboard') return <DashboardSkeleton />;
  if (path.startsWith('/leads')) return <LeadsSkeleton />;
  if (path === '/scheduling') return <SchedulingSkeleton />;
  return <GenericPageSkeleton />;
}

export default HorizontalLayout;
